/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PublicSeoPage from '../../components/shared/PublicSeoPage';
import PageMeta from '../../components/shared/PageMeta';

// The Data Deletion Instructions page.
//
// Meta App Review requires a URL that tells a person how to have their data
// deleted, and it is one of the two URLs that would have failed the
// instagram_content_publish submission (the other is the privacy policy).
// Google Play asks for the same thing for a published app. /data-deletion and
// /delete-account both served the marketing homepage before this.
//
// IT DESCRIBES A MANUAL PROCESS, because that is the true one. Checked in
// HARTOS integrations/social/api.py: there are DELETE routes for posts, votes,
// reactions and follows, and there is NO account deletion endpoint anywhere.
// Writing "click here to delete your account" would have been the easy thing
// and it would have been false, and a reviewer who clicks the button finds
// that out faster than anyone.
//
// When a self-serve deletion endpoint exists, this page should be the first
// thing that changes.

const STEPS = [
  {
    n: 'Local data on your own machine',
    p: 'Everything the desktop app holds lives on your device. Uninstalling Nunba and deleting its data folder removes it, and nothing needs to go through us because none of it was ever with us.',
  },
  {
    n: 'What your browser is holding',
    p: 'Signing in stores a token and an account id; using the site as a guest stores a guest id and a name; preferences are stored alongside them. Clearing site data for hevolve.ai in your browser removes all of it immediately.',
  },
  {
    n: 'Your account and anything you posted',
    p: 'Write to privacy@hevolve.ai from the email address on the account, with the subject "Delete my data". Say whether you want the whole account removed or only specific posts. We will confirm when it is done. If you are asking about an account you cannot sign in to, tell us and we will work out how to verify you without asking for more data than we already hold.',
  },
];

export default function DataDeletionPage() {
  return (
    <>
      <PageMeta
        title="Delete Your Data | Hevolve AI"
        description="How to have your Hevolve AI and Nunba data deleted: what lives only on your own machine, what your browser holds, and how to request removal of an account and its posts."
        ogTitle="Delete your data | Hevolve AI"
        ogDescription="What lives only on your machine, what your browser holds, and how to have an account and its posts removed."
        path="/data-deletion"
      >
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Delete your data',
          url: 'https://hevolve.ai/data-deletion',
          description: 'How to have your Hevolve AI account and data deleted.',
          publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' },
        })}</script>
      </PageMeta>

      <PublicSeoPage
        heading="Delete your data"
        headingVariant="h4"
        maxWidth="md"
        subheading="Three kinds of data, in three different places, and only one of them needs anything from us."
      >
        <Box sx={{ mb: 4 }}>
          {STEPS.map((s, i) => (
            <Box key={s.n} sx={{ display: 'flex', gap: 2.5, mb: 3, alignItems: 'flex-start' }}>
              <Box sx={{
                minWidth: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', bgcolor: 'rgba(23,192,127,0.14)', border: '1px solid rgba(23,192,127,0.4)',
                color: '#17c07f', fontWeight: 800, fontSize: '0.9rem',
              }}>
                {i + 1}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.75 }}>{s.n}</Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.85 }}>{s.p}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(240,178,58,0.07)', border: '1px solid rgba(240,178,58,0.24)', mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>Why there is no delete button yet</Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.85 }}>
            We have not built self-serve account deletion. Saying so is better than putting a button on this page
            that does not work, and a request handled by a person is a real route to the same outcome. When the
            endpoint exists this page changes first.
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.85, mb: 2 }}>
          What we hold in the first place, and what we do not, is set out in full on the privacy policy.
        </Typography>
        <Typography component={Link} to="/legal/privacy"
          sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#17c07f', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
          Read the privacy policy &rarr;
        </Typography>
      </PublicSeoPage>
    </>
  );
}
